import axios from "axios";
import Cookies from "js-cookie";
import { setAppUrl } from "../../actions/app";
import { resetStore, setCurrUser, updateCurrUser } from "../../actions/auth";
import { hideLoader, showLoader } from "../../actions/loader";
import { AUTH_TOKENS } from "../../config";
import { label, notifyIcon, requestMethod, requestStatus, storageKeys } from "../../config/constants";
import config from "../../env.config";
import { clearCookiesOnDomain, getCookies, removeCookiesOnDomain } from "../../helper/setCookies";
import store from "../../store/index";
import { checkApiSuccess, getNotification } from "../../utils/common";
import setAuthorizationToken from "../../utils/setAuthorizationToken";
import { setTenantOrganizationIdHeader } from "../../utils/setTenantOrganizationHeader";
import { handleErrResponse, handleFailureResponse } from "../error/api.error";
import { hasPendingRequests } from "./axiosInterceptor";
import { loadState, saveState } from "./session.service";

/**
 *
 * Handle API request throughout application
 * @author Himanshi Chawla
 */

/**
 *
 * @param {Object} requestPayload
 * @param {Function} dispatch Show/Hide Loader
 * @param {Boolean} notifications Show notifications to user on screen
 * @returns
 */
export const fetch = async (requestPayload, startLoader, notifications) => {
  try {
    appendToken();
    appendOrganizationHeader();
    if (startLoader) store.dispatch(showLoader());
    const response = await axios.request(requestPayload);
    if (!hasPendingRequests()) store.dispatch(hideLoader());
    return await handleResponse(response, notifications);
  } catch (err) {
    if (axios.isCancel(err)) {
      return err.code;
    }
    store.dispatch(hideLoader());
    handleApiError(err, notifications);
  }
};

/**
 * Append token to requests if JWT Token is present in cookies
 */
const appendToken = () => {
  const TOKEN = Cookies.get(AUTH_TOKENS.JWT_TOKEN);
  if (TOKEN) {
    setAuthorizationToken(TOKEN);
  }
};

/**
 * used to append organizationId to headers
 */
const appendOrganizationHeader = () => {
  const user = loadState(AUTH_TOKENS.USER);
  setTenantOrganizationIdHeader(user?.companyId);
};
/**
 * Handles API response
 * @param {Object} response
 * @param {Boolean} notifications
 * @returns
 */

const handleResponse = (response, notifications) => {
  if (response && (response.status === requestStatus.OK || response.status === requestStatus.POST_OK)) {
    if (notifications && response.data.message) getNotification(response.data.message, notifyIcon.SUCCESS_ICON);
    return response;
  } else if (notifications) handleFailureResponse(response.data);
};

export const storeFcmToken = (payload) => {
  return async (dispatch) => {
    try {
      console.log("storeFcmToken: Starting FCM token operation with payload:", payload);

      let requestConfig = {
        method: requestMethod.POST,
        url: `${config.BASE_URL.BASE_API_URL}/user/storeFcmToken`,
        data: payload,
      };

      let response = await fetch(requestConfig, true, false);
      console.log("storeFcmToken: API response:", response);

      if (checkApiSuccess(response)) {
        if (response?.data?.data) {
          const FcmTokenId = response?.data?.data[0];
          !payload?.isDeleted && dispatch(updateCurrUser(FcmTokenId));
        }
        console.log("storeFcmToken: FCM token operation completed successfully");
      } else {
        console.log("storeFcmToken: API call failed:", response);
      }
    } catch (error) {
      console.log("storeFcmToken: Error during FCM token operation:", error);
    }
  };
};

/**
 * handling api for logout user
 * @param {void}
 * @returns {user to remove}
 */

export const logoutUser = ({ skipRedirect = false } = {}) => {
  return async (dispatch, getState) => {
    console.log("logoutUser: Starting logout process");
    let isTenantDomainActive = await getCookies(AUTH_TOKENS.IS_TENANT_DOMAIN_ACTIVE);
    console.log("logoutUser: Starting FCM token cleanup");
    await setStateOnLogout(dispatch, getState);
    console.log("logoutUser: FCM token cleanup completed");

    sessionStorage.removeItem("redirectUrl");

    /** Check if we're currently on a tenant domain*/
    const currentDomain = window.location.origin;
    const isOnTenantDomain = currentDomain !== config.BASE_URL.APP_URL;

    // Clear cookies common for both cases
    clearCookiesOnDomain(AUTH_TOKENS.IS_TENANT_DOMAIN_ACTIVE);
    clearCookiesOnDomain(AUTH_TOKENS.JWT_TOKEN);
    clearCookiesOnDomain(AUTH_TOKENS.REFRESH_TOKEN);
    clearCookiesOnDomain(AUTH_TOKENS.USER);
    clearCookiesOnDomain(AUTH_TOKENS.TENANT_DOMAIN);

    console.log("logoutUser: Redirecting to login page");
    if (!skipRedirect && (isTenantDomainActive || isOnTenantDomain)) {
      // Redirect to current domain's login page (tenant login)
      store.dispatch(setAppUrl(currentDomain));
      await window.location.replace(`${currentDomain}/login`);
    }
  };
};
/**
 * common function for state set on logout
 * @param {Object}
 * @author Himanshi
 */
const setStateOnLogout = async (dispatch, getState) => {
  const { auth } = getState();

  /* Wait for FCM token cleanup to complete with timeout */
  if (auth?.user?.id) {
    console.log("setStateOnLogout: Cleaning up FCM token for user:", auth.user.id);

    try {
      const fcmCleanupPromise = dispatch(storeFcmToken({ userId: auth?.user.id, isDeleted: 1, fcmTokenId: auth?.user?.FcmTokenId }));
      const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error("FCM cleanup timeout")), 5000));
      await Promise.race([fcmCleanupPromise, timeoutPromise]);
      console.log("setStateOnLogout: FCM token cleanup completed");
    } catch (error) {
      console.log("setStateOnLogout: FCM token cleanup failed or timed out:", error);
    }
  } else {
    console.log("setStateOnLogout: No user ID found, skipping FCM token cleanup");
  }

  setAuthorizationToken(false);
  dispatch(setCurrUser({}));
  saveState(AUTH_TOKENS.USER, "");
  localStorage.removeItem(label.USER_PREFERENCE);
  localStorage.removeItem(label.USER_DASHBOARD_DURATION_OPTION);
  localStorage.removeItem(storageKeys.JOB_ROLES_SORT_SETTING);
  dispatch(resetStore());
  // sessionStorage.removeItem("redirectUrl");
};

/**
 * Handles Exceptions for API call
 * @param {Object} err
 * @param {Boolean} notifications
 */
const handleApiError = (err, notifications) => {
  if (err.response && err.response.status == requestStatus.FORBIDDEN) {
    const TOKEN = Cookies.get(AUTH_TOKENS.JWT_TOKEN);
    if (TOKEN) {
      store.dispatch(logoutUser());
    }
  } else if (notifications) handleErrResponse(err);
};
